import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import './OurServices.css';

// ── SECTION 2 DATA: Category cards ───────────────────────────
// Three main service categories shown as icon cards.
const CATEGORIES = [
  {
    id: 1,
    icon: './Images/Life_in.jpeg',
    name: 'LIFE',
    items: [
      'Term Life Insurance | Mortgage Protection',
      'Indexed Universal Life (IUL)',
      'Kids Education Planning',
      'Tax-Free Retirement Planning',
    ],
  },
  {
    id: 2,
    icon: './Images/health_in.jpeg',
    name: 'HEALTH',
    items: [
      'ACA - Affordable Care Act',
      'Medicare Supplements & Advantages',
      'Individual & Family Health Plans',
      'Coverage for Every Stage of Life',
    ],
  },
  {
    id: 3,
    icon: './Images/wealth_in.jpeg',
    name: 'WEALTH',
    items: [
      'Fixed Indexed Annuities (FIA)',
      'Tax-Advantage Plans',
      'Real Estate - Land Banking Parcels',
      'Stock Market - Active Wealth Management',
      'Estate Planning',
    ],
  },
];

// ── SECTION 3 DATA: Detailed service list ────────────────────
// Two columns of services with name + description.
const LIFE_HEALTH_SERVICES = [
  {
    id: 1,
    name: 'Term Life Insurance | Mortgage Protection',
    desc: '10, 20, 30, 35 Yr Term | Living Benefits | Return-on-Premium',
  },
  {
    id: 2,
    name: 'Indexed Universal Life (IUL)',
    desc: 'Universal Life | Cash Value | Long Term Care | Living Benefits | College Education Plan',
  },
  {
    id: 3,
    name: 'Fixed Indexed Annuity | Tax-Free Retirement Plan',
    desc: '401K Rollovers | IRAs | Annuities | Retirement Vehicles',
  },
  {
    id: 4,
    name: 'Estate Planning | Will & Trust',
    desc: 'Easy Online Comprehensive Estate Plan — fast, affordable, and legally reliable.',
  },
  {
    id: 5,
    name: 'Real Estate | Land Banking Parcels',
    desc: 'Buy Premium Lands for Investment in Southern California | Referral',
  },
  {
    id: 6,
    name: 'Stock Market | Wealth Management',
    desc: 'Active Money Management | Stocks | Bonds | ETFs | Referral',
  },
];

const HEALTH_SERVICES = [
  {
    id: 1,
    name: 'ACA - Affordable Care Act',
    desc: 'Comprehensive health coverage for individuals and families without employer-sponsored insurance.',
  },
  {
    id: 2,
    name: 'Medicare Supplements & Advantages',
    desc: 'Essential options for those turning 65 — covering what Medicare doesn\'t, with competitive choices.',
  },
  {
    id: 3,
    name: 'Individual & Family Health Plans',
    desc: 'Customized health coverage plans designed to fit your budget and your family\'s specific needs.',
  },
];

// ── SECTION 4 DATA: CTA banner ───────────────────────────────
const CTA = {
  badge:   'New Opportunity',
  title:   'Start Your Own Business with',
  titleHL: 'Will to Wealth!',
  body: [
    'Ready to earn $5K–$10K/month part-time or full-time?',
    'Join the financial services industry — build legacy income with full training and support.',
    '📞 Curious to learn more? Book a Zoom session or visit us in person!',
  ],
  emphasis: 'Let\'s rise together. See you at the top!',
  primaryLabel:   'Book a Free Session',
  primaryLink:    'https://bit.ly/2Z6BE07',
  secondaryLabel: 'Learn More',
  secondaryTo:    '/consulting',
};

// ✅ NEW — renders icon as an <img> tag
function CategoryCard({ cat, active, onClick }) {
  return (
    <div
      className={`services-cat-card ${active ? 'active' : ''}`}
      onClick={onClick}
      style={{
        borderColor: active
          ? 'rgba(201, 168, 76, 0.6)'
          : 'rgba(255, 255, 255, 0.06)',
        boxShadow: active
          ? '0 16px 48px rgba(201, 168, 76, 0.2)'
          : 'none',
        cursor: 'pointer',
      }}
    >
      {/* Icon image box — renders the image path as <img> */}
      <div
        className="services-cat-icon"
        style={{
          background: active
            ? 'rgba(201, 168, 76, 0.2)'
            : 'rgba(201, 168, 76, 0.1)',
        }}
      >
        <img
          src={cat.icon}              // ← uses the image path from CATEGORIES
          alt={cat.name}              // ← accessibility: "LIFE", "HEALTH", "WEALTH"
          className="services-cat-icon-img"
          onError={e => {
            // If image fails to load, hide it silently
            // so the card doesn't show a broken image icon
            e.target.style.display = 'none';
          }}
        />
      </div>

      <div className="services-cat-name">{cat.name}</div>

      <ul className="services-cat-list">
        {cat.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
} 



// ── COMPONENT: Single Service Detail Row ──────────────────────
// Renders one service with its name and description.
function ServiceDetailItem({ service }) {
  return (
    <div className="services-detail-item">
      <div className="services-detail-name">{service.name}</div>
      <div className="services-detail-desc">{service.desc}</div>
    </div>
  );
}


// ── MAIN PAGE COMPONENT ───────────────────────────────────────
function OurServices() {

  // activeCard: tracks which category card is highlighted.
  // Starts with card 1 (Life) selected by default.
  const [activeCard, setActiveCard] = useState(1);

  return (
    <div className="services-page">

      {/* ════════════════════════════════════════════════
          SECTION 1 — HERO BANNER
          ════════════════════════════════════════════════ */}
      <section className="services-hero">
        <div className="services-hero-inner">

          {/* Gold pill badge */}
          <span className="services-hero-eyebrow">
            Will to Wealth Financial Group
          </span>

          {/* Main title */}
          <h1 className="services-hero-title">
            Business Opportunity in the{' '}
            <span>Financial Domain</span>
          </h1>

          {/* Subtitle paragraph */}
          <p className="services-hero-subtitle">
            Our comprehensive business model provides an accessible pathway
            to financial freedom — offering one-on-one guidance, educational
            resources, and ongoing support to help our agents achieve their goals.
            With our proven model, we make it possible for people to realize their
            dreams of financial independence and business ownership.
          </p>

        </div>
      </section>


      {/* ════════════════════════════════════════════════
          SECTION 2 — THREE CATEGORY CARDS (Life|Health|Wealth)
          ════════════════════════════════════════════════ */}
      <section className="services-categories">
        <div className="services-categories-inner">

          {/* Section label + title */}
          <span className="services-section-label">What We Offer</span>
          <h2 className="services-section-title">Our Services</h2>

          {/* Three cards — clicking one highlights it */}
          <div className="services-cat-grid">
            {CATEGORIES.map(cat => (
              <CategoryCard
                key={cat.id}
                cat={cat}
                active={activeCard === cat.id}
                onClick={() => setActiveCard(cat.id)}
              />
            ))}
          </div>

        </div>
      </section>


      <section className="services-detail">
        <div className="services-detail-inner">

          {/* Section label + title */}
          <span className="services-section-label">In Detail</span>
          <h2 className="services-section-title">Financial Services</h2>

          {/* Two column grid */}
          <div className="services-detail-grid">

            {/* LEFT COLUMN — Life & Health full list */}
            <div>
              <div className="services-detail-col-title">
                Life &amp; Health Services
              </div>
              {LIFE_HEALTH_SERVICES.map(service => (
                <ServiceDetailItem key={service.id} service={service} />
              ))}

              {/* Tagline at bottom of left column */}
              <div style={{
                marginTop: 32,
                paddingTop: 24,
                borderTop: '1px solid rgba(201,168,76,0.15)',
                fontSize: 13,
                color: '#c9a84c',
                fontStyle: 'italic',
                lineHeight: 1.7,
                fontFamily: 'Outfit, sans-serif',
              }}>
                Will to Wealth Financial Services — Create Wealth for Families.
                No family left behind.
              </div>
            </div>

            {/* RIGHT COLUMN — Health only */}
            <div>
              <div className="services-detail-col-title">
                Health Services
              </div>
              {HEALTH_SERVICES.map(service => (
                <ServiceDetailItem key={service.id} service={service} />
              ))}

              {/* Small info box */}
              <div style={{
                marginTop: 32,
                background: 'rgba(201,168,76,0.06)',
                border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: 12,
                padding: '20px 24px',
              }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#c9a84c',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  marginBottom: 8,
                  fontFamily: 'Outfit, sans-serif',
                }}>
                  Need Help Choosing?
                </div>
                <div style={{
                  fontSize: 13,
                  color: '#8a9bb0',
                  lineHeight: 1.7,
                  fontFamily: 'Outfit, sans-serif',
                }}>
                  Our licensed professionals will walk you through every
                  option and help you find the plan that fits your family
                  and your budget.
                </div>
                
                  <a href="https://bit.ly/2Z6BE07"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-block',
                    marginTop: 14,
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#c9a84c',
                    textDecoration: 'none',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontFamily: 'Outfit, sans-serif',
                  }}
                >
                  Book a Free Consultation →
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ════════════════════════════════════════════════
          SECTION 4 — CTA / ANNOUNCEMENT BANNER
          ════════════════════════════════════════════════ */}
      <section className="services-cta">
        <div className="services-cta-inner">
          <div className="services-cta-box">

            {/* Badge */}
            <div className="services-cta-badge">{CTA.badge}</div>

            {/* Title */}
            <h2 className="services-cta-title">
              {CTA.title}{' '}
              <span>{CTA.titleHL}</span>
            </h2>

            {/* Body paragraphs */}
            <div className="services-cta-body">
              {CTA.body.map((line, i) => (
                <p key={i} style={{ marginBottom: i < CTA.body.length - 1 ? 10 : 0 }}>
                  {line}
                </p>
              ))}
              <p style={{ marginTop: 12 }}>
                <strong>{CTA.emphasis}</strong>
              </p>
            </div>

            {/* Two buttons */}
            <div className="services-cta-buttons">

              {/* Primary — external link (opens in new tab) */}
              
                <a href={CTA.primaryLink}
                target="_blank"
                rel="noreferrer"
                className="services-cta-btn-primary"
              >
                {CTA.primaryLabel} →
              </a>

              {/* Secondary — internal NavLink */}
              <NavLink
                to={CTA.secondaryTo}
                className="services-cta-btn-secondary"
              >
                {CTA.secondaryLabel}
              </NavLink>

            </div>

          </div>
        </div>
      </section>

    </div>
  );
}

export default OurServices;